import { request } from "@/api/client";
import type {
  CrmContact,
  CrmEmailDraft,
  CrmLead,
  EmailTemplate,
  EmailTemplateVersion,
  IngestedVenue,
  OutboundEmail,
  BandsintownDigestVenue,
} from "@/types/crm";

export const MOCK_LEADS: CrmLead[] = [
  {
    id: 101,
    title: "The Fillmore • Spring routing hold",
    source: "Memory Browser",
    status: "open",
    priority: 3,
    pipeline_stage: "prospecting",
    agent_owner: "T. Allen",
    region: "West",
    event_window: { start: "2025-04-10", end: "2025-04-14" },
    description: "House promoter requested routing options after SF festival play.",
    created_at: "2024-11-01T10:00:00Z",
    updated_at: "2024-11-05T12:00:00Z",
  },
  {
    id: 102,
    title: "Brooklyn Steel • Summer 2025 co-pro",
    source: "Bandsintown",
    status: "open",
    priority: 2,
    pipeline_stage: "outreach",
    agent_owner: "S. Wood",
    region: "Northeast",
    event_window: { start: "2025-07-15", end: "2025-07-18" },
    description: "Venue responded to initial outreach, awaiting offer sheet.",
    created_at: "2024-10-15T09:00:00Z",
    updated_at: "2024-11-03T15:24:00Z",
  },
  {
    id: 103,
    title: "ACL Live • Fall anchor date",
    source: "CRM",
    status: "open",
    priority: 1,
    pipeline_stage: "negotiation",
    agent_owner: "C. Flores",
    region: "South",
    event_window: { start: "2025-09-05", end: "2025-09-07" },
    description: "Offer received, finalizing support budget and split details.",
    created_at: "2024-09-28T14:30:00Z",
    updated_at: "2024-11-04T19:00:00Z",
  },
];

type ContactsResponse = { contacts: CrmContact[]; total: number; page?: number; pages?: number; limit?: number };
type LeadsResponse = { leads: CrmLead[] };
type TemplatesResponse = { templates: EmailTemplate[] };
type SystemTemplateResponse = { body: string };
export type SystemTemplateInfo = {
  id: string;
  label: string;
  description?: string | null;
  filename: string;
};
type IngestedVenuesResponse = { venues: IngestedVenue[] };
type TemplateVariablesResponse = { type: string; variables: Record<string, string> };

export type ContactFilters = {
  query?: string;
  tags?: string;
  region?: string;
  contactType?: string;
  sort?: string;
  order?: string;
  page?: number;
  limit?: number;
};

export type LeadCreatePayload = {
  contact_id?: number | null;
  title?: string | null;
  source?: string | null;
  status?: string;
  priority?: number;
  pipeline_stage?: string;
  agent_owner?: string | null;
  region?: string | null;
  event_window?: { start: string; end: string } | null;
  description?: string | null;
  metadata?: Record<string, unknown> | null;
};

export async function fetchContacts(filters: ContactFilters = {}): Promise<ContactsResponse> {
  const params = new URLSearchParams();
  if (filters.query) params.set("query", filters.query);
  if (filters.tags) params.set("tags", filters.tags);
  if (filters.region) params.set("region", filters.region);
  if (filters.contactType) params.set("contactType", filters.contactType);
  if (filters.sort) params.set("sort", filters.sort);
  if (filters.order) params.set("order", filters.order);
  if (filters.page) params.set("page", String(filters.page));
  if (filters.limit) params.set("limit", String(filters.limit));
  const suffix = params.toString();
  const data = await request<ContactsResponse>(`/contacts${suffix ? `?${suffix}` : ""}`);
  const total =
    typeof data?.total === "number"
      ? data.total
      : data?.contacts?.length ?? 0;
  return {
    contacts: data?.contacts ?? [],
    total,
    page: data?.page,
    pages: data?.pages,
    limit: data?.limit,
  };
}

export async function fetchContact(contactId: number): Promise<CrmContact> {
  return request<CrmContact>(`/contacts/${contactId}`);
}

export type CrmActivityLog = {
  id: number;
  action_type: string;
  summary?: string | null;
  agent_name?: string | null;
  metadata?: Record<string, unknown>;
  created_at: string;
};

export async function fetchContactActivity(contactId: number, limit = 50): Promise<CrmActivityLog[]> {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  return request<CrmActivityLog[]>(`/contacts/${contactId}/activity?${params.toString()}`);
}

export type ContactCreatePayload = {
  name: string;
  contact_name?: string | null;
  company_name?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  contact_type?: string | null;
  role_id?: string | null;
  tags?: string[];
  source_url?: string | null;
  notes?: string | null;
};

export function createContact(payload: ContactCreatePayload): Promise<CrmContact> {
  return request<CrmContact>("/contacts", {
    method: "POST",
    body: payload,
  });
}

export async function fetchLeads(filters: { status?: string; stage?: string } = {}): Promise<CrmLead[]> {
  const params = new URLSearchParams();
  if (filters.status) params.set("status", filters.status);
  if (filters.stage) params.set("stage", filters.stage);
  params.set("limit", "250");
  const suffix = params.toString();
  const data = await request<LeadsResponse>(`/leads${suffix ? `?${suffix}` : ""}`);
  return data?.leads ?? [];
}

/** Read one artist-scoped contact (existing GET /contacts/{id}). */
export function fetchCrmContact(contactId: number | string): Promise<CrmContact> {
  return request<CrmContact>(`/contacts/${encodeURIComponent(String(contactId))}`);
}

export function fetchLeadDetail(leadId: number | string): Promise<CrmLead> {
  return request<CrmLead>(`/leads/${encodeURIComponent(String(leadId))}`);
}

export function createLead(payload: LeadCreatePayload): Promise<CrmLead> {
  return request<CrmLead>("/leads", {
    method: "POST",
    body: payload,
  });
}

export async function fetchTemplates(includeVersions = true): Promise<EmailTemplate[]> {
  const params = new URLSearchParams();
  if (includeVersions) params.set("includeVersions", "1");
  const suffix = params.toString();
  const data = await request<TemplatesResponse>(`/templates${suffix ? `?${suffix}` : ""}`);
  return data?.templates ?? [];
}

export function fetchSystemTemplates(): Promise<SystemTemplateInfo[]> {
  return request<SystemTemplateInfo[]>("/templates/system");
}

export function fetchSystemTemplateBody(templateId: string): Promise<SystemTemplateResponse> {
  return request<SystemTemplateResponse>(`/templates/system/${templateId}`);
}

export function updateSystemTemplateBody(templateId: string, body: string): Promise<SystemTemplateResponse> {
  return request<SystemTemplateResponse>(`/templates/system/${templateId}`, {
    method: "PUT",
    body: { body },
  });
}

export function fetchTemplateVariables(templateType: string): Promise<TemplateVariablesResponse> {
  const params = new URLSearchParams();
  if (templateType) params.set("type", templateType);
  const suffix = params.toString();
  return request<TemplateVariablesResponse>(`/templates/variables?${suffix}`);
}

export async function fetchIngestedVenues(query?: string, limit = 100): Promise<IngestedVenue[]> {
  const params = new URLSearchParams();
  if (query) params.set("query", query);
  params.set("limit", String(limit));
  const suffix = params.toString();
  const data = await request<IngestedVenuesResponse>(`/ingestor/venues${suffix ? `?${suffix}` : ""}`);
  return data?.venues ?? [];
}

export function enrichCrmContact(contactId: number) {
  return request(`/booking/enrich/${encodeURIComponent(String(contactId))}`, { method: "POST" });
}

export function enrichAllCrmContacts() {
  return request(`/booking/enrich/all`, { method: "POST" });
}

export type BandsintownHistoryEntry = {
  artist_id: string;
  artist_name?: string | null;
  last_ingested_at?: string | null;
};

export function fetchBandsintownHistory(limit = 25): Promise<BandsintownHistoryEntry[]> {
  const params = new URLSearchParams();
  if (limit) params.set("limit", String(limit));
  const suffix = params.toString();
  return request<BandsintownHistoryEntry[]>(
    `/booking/bandsintown/history${suffix ? `?${suffix}` : ""}`,
  );
}

export function findSimilarArtists(contactId: number) {
  return request(`/contacts/${encodeURIComponent(String(contactId))}/similar-artists`, {
    method: "POST",
  });
}

export type SimilarResolvedArtist = {
  id: number;
  name: string;
  spotify_id: string;
  bandsintown_slug?: string | null;
  tracker_count?: number | null;
  genre?: string | null;
  source: string;
  crm_contact_id?: number | null;
  imported_at?: string | null;
  created_at: string;
  seed_resolved_artist_id?: number | null;
  seed_name?: string | null;
};

export function fetchSimilarResolvedArtists(contactId: number) {
  const params = new URLSearchParams();
  params.set("contact_id", String(contactId));
  return request<{ artists: SimilarResolvedArtist[]; total: number }>(
    `/artist-resolver/resolved-artists/similar?${params.toString()}`,
  );
}

export function ingestResolvedArtist(artistId: number) {
  return request(`/artist-resolver/resolved-artists/${artistId}/ingest`, {
    method: "POST",
  });
}

export function digestCrmVenue(contactId: number) {
  return request(`/venues/${encodeURIComponent(String(contactId))}/digest`, { method: "POST" });
}

export type LeadEmailSummary = {
  lead_id: number;
  last_email_at?: string | null;
  unreplied_count: number;
  has_open_thread: boolean;
};

export function fetchLeadEmailSummary(leadIds: number[]): Promise<{ items: LeadEmailSummary[] }> {
  return request<{ items: LeadEmailSummary[] }>("/leads/email-summary", {
    method: "POST",
    body: { lead_ids: leadIds },
  });
}

export function linkLeadEmailThread(leadId: number, payload: { thread_id: string }) {
  return request<CrmLead>(`/leads/${encodeURIComponent(String(leadId))}/emails/link`, {
    method: "POST",
    body: payload,
  });
}

export function createLeadFromEmailThread(payload: {
  thread_id: string;
  title?: string | null;
  pipeline_stage?: string | null;
  region?: string | null;
  event_window?: { start: string; end: string } | null;
  description?: string | null;
}) {
  return request<CrmLead>("/leads/from-email-thread", {
    method: "POST",
    body: payload,
  });
}

export type ResolvedArtistIngestResponse = {
  processed: number;
  successes: number;
  failures: number;
  results: Array<{
    artist_id: number;
    bandsintown_slug?: string | null;
    inserted_events?: number | null;
    venues_touched?: number | null;
    errors?: string[];
  }>;
};

export function ingestResolvedArtistVenues(limit = 25) {
  return request<ResolvedArtistIngestResponse>("/ingestor/venues/resolved-artists", {
    method: "POST",
    body: { limit },
  });
}

export type BandsintownDigestResponse = {
  slug: string;
  resolved_artist_id?: number | null;
  venues: BandsintownDigestVenue[];
  errors: string[];
};

export function fetchBandsintownDigest(payload: { bandsintown_id: string; window: "upcoming" | "past" | "all" }) {
  return request<BandsintownDigestResponse>("/ingestor/bandsintown/digest", {
    method: "POST",
    body: payload,
  });
}

export type BandsintownDigestSendVenue = {
  digest_id: string;
  venue_name: string;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  ticket_url?: string | null;
  event_date?: string | null;
  overrides?: BandsintownDigestOverride;
};

export type BandsintownDigestSendPayload = {
  slug: string;
  venues: BandsintownDigestSendVenue[];
  tags?: string[];
  artist_name?: string | null;
};

export function sendBandsintownDigest(payload: BandsintownDigestSendPayload) {
  return request<Array<{ digest_id: string; status: string; crm_contact_id?: number | null; error?: string | null }>>(
    "/ingestor/bandsintown/digest/send",
    {
      method: "POST",
      body: payload,
    },
  );
}

export async function fetchDraftEmails(limit = 20): Promise<CrmEmailDraft[]> {
  const params = new URLSearchParams();
  params.set("status", "draft");
  params.set("limit", String(limit));
  const suffix = params.toString();
  const data = await request<{ emails: CrmEmailDraft[] }>(`/emails?${suffix}`);
  return data?.emails ?? [];
}

export function createDraftEmail(payload: {
  lead_id: number;
  campaign_id?: number | null;
  template_id?: number | null;
  version_strategy?: string | null;
  personalization?: Record<string, unknown> | null;
}): Promise<OutboundEmail> {
  return request<OutboundEmail>("/emails/draft", {
    method: "POST",
    body: payload,
  });
}

export function favoriteDraftEmail(
  emailId: number,
  payload: { template_id?: number | null; name?: string | null; persona?: string | null; channel?: string | null; notes?: string | null },
): Promise<EmailTemplate> {
  return request<EmailTemplate>(`/emails/${encodeURIComponent(emailId)}/favorite`, {
    method: "POST",
    body: payload,
  });
}

export type SendVenuePayload = {
  contact_name?: string;
  email?: string;
  phone?: string;
  website?: string;
  address?: string;
  notes?: string;
  social_links?: string[];
  tags?: string[];
};

export type BandsintownDigestOverride = {
  contact_name?: string;
  email?: string;
  phone?: string;
  website?: string;
  notes?: string;
};

export function sendVenueToCrm(showId: string, payload: SendVenuePayload) {
  return request<CrmContact>(`/ingestor/venues/${encodeURIComponent(showId)}/send-to-crm`, {
    method: "POST",
    body: payload,
  });
}

export type UpdateContactPayload = Partial<
  Pick<
    CrmContact,
    | "name"
    | "contact_name"
    | "company_name"
    | "email"
    | "phone"
    | "website"
    | "location_city"
    | "location_region"
    | "country"
    | "address_line"
    | "notes"
    | "tags"
    | "source_urls"
    | "social_links"
    | "contact_type"
    | "role_id"
    | "crm_stage"
    | "owner"
  >
>;

export function updateContact(contactId: number, payload: UpdateContactPayload) {
  return request<CrmContact>(`/contacts/${contactId}`, {
    method: "PATCH",
    body: payload,
  });
}

export function deleteContact(contactId: number) {
  return request(`/contacts/${contactId}`, {
    method: "DELETE",
  });
}

export type BulkImportPayload = {
  contacts: Array<
    Partial<Pick<CrmContact, "name" | "contact_name" | "company_name" | "email" | "phone" | "website" | "notes">> & {
      location_city?: string | null;
      location_region?: string | null;
      country?: string | null;
      address_line?: string | null;
      tags?: string[];
      source_urls?: string[];
      social_links?: string[];
    }
  >;
};

export function importContacts(payload: BulkImportPayload) {
  return request<{ created: number; updated: number }>("/contacts/import", {
    method: "POST",
    body: payload,
  });
}

export type ManualContactPayload = {
  display_name?: string | null;
  company_name?: string | null;
  contact_type: "Person" | "Organization" | "Venue" | "Media" | "Other";
  role_id: string;
  crm_stage?: string | null;
  email?: string | null;
  phone?: string | null;
  url?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  tags?: string[];
  notes?: string | null;
  source_url?: string | null;
  owner?: string | null;
};

export function createManualContact(payload: ManualContactPayload) {
  return request<CrmContact>("/contacts/manual", {
    method: "POST",
    body: payload,
  });
}

export type TemplateCreatePayload = {
  name: string;
  channel: string;
  persona?: string | null;
  subject: string;
  body: string;
};

export function createTemplate(payload: TemplateCreatePayload) {
  const body = {
    name: payload.name,
    channel: payload.channel,
    persona: payload.persona,
    initial_version: {
      subject: payload.subject,
      body: payload.body,
    },
  };
  return request<EmailTemplate>("/templates", { method: "POST", body });
}

export function updateTemplate(templateId: number, payload: Partial<Pick<EmailTemplate, "name" | "channel" | "persona">>) {
  return request<EmailTemplate>(`/templates/${templateId}`, {
    method: "PATCH",
    body: payload,
  });
}

export function deleteTemplate(templateId: number) {
  return request(`/templates/${templateId}`, { method: "DELETE" });
}

export type TemplateVersionPayloadInput = {
  subject: string;
  body: string;
  active?: boolean;
};

export function createTemplateVersion(templateId: number, payload: TemplateVersionPayloadInput) {
  return request<EmailTemplateVersion>(`/templates/${templateId}/versions`, {
    method: "POST",
    body: {
      subject: payload.subject,
      body: payload.body,
      active: payload.active ?? true,
    },
  });
}

export function updateTemplateVersion(
  templateId: number,
  versionId: number,
  payload: Partial<{ subject: string; body: string; active: boolean }>,
) {
  return request<EmailTemplateVersion>(`/templates/${templateId}/versions/${versionId}`, {
    method: "PATCH",
    body: payload,
  });
}

export function deleteTemplateVersion(templateId: number, versionId: number) {
  return request(`/templates/${templateId}/versions/${versionId}`, { method: "DELETE" });
}

export function cloneTemplateVersion(templateId: number, version?: number) {
  return request<EmailTemplateVersion>(`/templates/${templateId}/clone`, {
    method: "POST",
    body: version ? { version } : {},
  });
}

export type CrmLeadUpdatePayload = Partial<
  Pick<
    CrmLead,
    | "status"
    | "pipeline_stage"
    | "description"
    | "agent_owner"
    | "title"
    | "region"
    | "priority"
    | "event_window"
  >
> & {
  metadata?: Record<string, unknown>;
};

export function updateCrmLead(id: number, payload: CrmLeadUpdatePayload) {
  return request<CrmLead>(`/leads/${id}`, {
    method: "PATCH",
    body: payload,
  });
}

export type ContactUploadDocument = {
  filename: string;
  format: "text" | "csv" | "pdf";
  content: string;
  encoding?: string;
  metadata?: Record<string, string>;
};

export type ContactUploadResult = {
  processed: number;
  created: number;
  updated: number;
};

export type ContactUploadResponse = {
  result: ContactUploadResult;
  contacts: Array<{
    name?: string | null;
    email?: string | null;
    company?: string | null;
  }>;
};

export function uploadContacts(payload: { documents: ContactUploadDocument[] }) {
  return request<ContactUploadResponse>("/crm/contacts/upload", {
    method: "POST",
    body: payload,
  });
}

// ---------------------------------------------------------------------------
// Booking composer (musician outreach). Shares the lead's outbound_emails draft with
// Discover Pitch. Template fill is server-side via the CRM variable registry.
// ---------------------------------------------------------------------------

export type LeadOutreachDraft = {
  id: number;
  subject: string;
  body: string;
  recipient: string | null;
  updated_at: string | null;
};

export type LeadOutreachState = {
  lead_id: number;
  contact: { name: string | null; email: string | null };
  contacted_at: string | null;
  draft: LeadOutreachDraft | null;
  recommended_template_kind: "initial" | "routing" | "follow_up" | "returning";
};

export type LeadTemplateFill = {
  template_id: number;
  template_version_id: number;
  template_name: string;
  subject: string;
  body: string;
  missing: string[];
  unknown: string[];
};

export function fetchLeadOutreach(leadId: number) {
  return request<LeadOutreachState>(`/leads/${encodeURIComponent(String(leadId))}/outreach`);
}

export function saveLeadOutreachDraft(
  leadId: number,
  payload: { subject: string; body: string; recipient?: string | null; template_version_id?: number | null },
) {
  return request<{ draft: LeadOutreachDraft }>(`/leads/${encodeURIComponent(String(leadId))}/outreach/draft`, {
    method: "PUT",
    body: payload,
  });
}

export function sendLeadOutreach(
  leadId: number,
  payload: {
    subject: string;
    body: string;
    recipient: string;
    template_version_id?: number | null;
    idempotency_key: string;
  },
) {
  return request<{
    email: LeadOutreachDraft;
    status: string;
    from_address: string | null;
    crm_sync: {
      status: "advanced" | "already_outreach" | "preserved_later_stage" | "not_applicable" | "pending";
      booking_lead_id: number | null;
      stage: string | null;
    };
  }>(`/leads/${encodeURIComponent(String(leadId))}/outreach/send`, {
    method: "POST",
    body: payload,
  });
}
export function applyLeadOutreachTemplate(leadId: number, templateId: number) {
  return request<LeadTemplateFill>(`/leads/${encodeURIComponent(String(leadId))}/outreach/template`, {
    method: "POST",
    body: { template_id: templateId },
  });
}

/** Outreach records (outbound_emails) for one lead, newest first. */
export function fetchLeadOutboundEmails(leadId: number) {
  return request<OutboundEmail[]>(`/leads/${encodeURIComponent(String(leadId))}/emails?limit=20`);
}
